import { FontFamilies } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { useContext } from 'react';
import ReactMaskedInput, { Mask, MaskedInputProps as ReactMaskedInputProps } from 'react-text-mask';
import { WrappedFieldProps } from 'redux-form';
import Input from 'shared/components/Input';
import Markdown from 'shared/components/Markdown';
import MaterialInput, { MATERIAL_INPUT_HEIGHT } from 'shared/components/MaterialInput';
import { fieldStyles } from 'shared/styles/form';
import { SPACINGS } from 'shared/styles/variables';
import styled, { ThemeContext } from 'styled-components';

import Button from 'design-system/components/Button';
import { errorStyles } from 'inquiry/mixins/form';
import useToggle from 'lib/hooks/useToggle';
import { InputStyles } from 'lib/interfaces/input-styles';
import { Z_INDICES } from 'verify/styles/variables';

const FormError = styled('div')`
  ${errorStyles}
`;

const Container = styled('div')`
  // NOTE this is needed for material inputs
  position: relative;
`;

export interface ThemedMaskedInputProps extends ReactMaskedInputProps {
  mask: Mask;
  label?: string;
  secure?: boolean;
}

const StyledThemedMaskedInput = styled(ReactMaskedInput)`
  ${(props) => fieldStyles(props, props.theme)};
  font-family: ${FontFamilies.Monospace};

  /* Override field styles, which are required to position it */
  left: 0;
  position: absolute;

  background: transparent !important;
  border-color: transparent;
  /* We use the masked input cursor but hide the text, relying on DisplayInput instead */
  caret-color: #000000;
  // fieldStyles overrides -webkit-text-fill-color, which gets applied with higher priority than
  // color, so need to override the attribute here as well
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
`;

const DisplayInput = styled(Input)`
  ${(props) => fieldStyles(props, props.theme)};
  font-family: ${FontFamilies.Monospace};
`;

const StyledMaterialThemedMaskedInput = styled(ReactMaskedInput)`
  width: 100%;
  padding: ${SPACINGS.base}px ${SPACINGS.base}px ${SPACINGS.base}px ${SPACINGS.base / 2}px;
  font-family: ${FontFamilies.Monospace};

  /* Override field styles, which are required to position it */
  left: 0;
  position: absolute;

  /**
    * Unlike for the outlined input, the display input is in front. So we rely on
    * the rearmost input for the background-color.
    */
  background-color: ${({ theme }) => theme.colorInputBackground};
  border-color: transparent;
  /* We use the masked input cursor but hide the text, relying on DisplayInput instead */
  caret-color: #000000;
  color: transparent !important;
`;

const DisplayMaterialInput = styled(MaterialInput)`
  /**
    * The absolutely positioned material input is in front of the absolutely
    * positioned masked input. We can't avoid this, because the container needs
    * to be relatively positioned for the input to have the right height. Make
    * the display input unclickable, and make the masked input responsible for
    * rendering the background.
    */
  background-color: transparent !important;
  pointer-events: none;
  > input {
    font-family: ${FontFamilies.Monospace};
    background-color: transparent !important;
  }
`;

// Containers copied from ThemedFormInput.tsx
const OutlinedContainer = styled('div')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const MaterialContainer = styled('div')`
  margin-bottom: ${SPACINGS.base}px;

  // NOTE this is needed for material inputs
  position: relative;
  padding-top: ${SPACINGS.base * 2}px;
`;

const MaterialFormError = styled('div')`
  ${errorStyles};

  // TODO: make this a variable based on input height
  top: ${MATERIAL_INPUT_HEIGHT + SPACINGS.base}px;
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 0;
  z-index: ${Z_INDICES.inputButtons};
  height: 100%;
  display: flex;
  align-items: center;
  padding-right: ${SPACINGS.base}px;

  [dir='rtl'] & {
    right: unset;
    left: 0;
    padding-right: unset;
    padding-left: ${SPACINGS.base}px;
  }
`;

const ToggleButton = styled(Button)`
  background: none;

  svg {
    color: ${({ theme }) => theme.colorFont};
  }
`;

const MaterialButtonContainer = styled(ButtonContainer)`
  height: ${MATERIAL_INPUT_HEIGHT}px;
`;

const ThemedMaskedInput = ({
  meta: { error, submitFailed },
  input,
  mask,
  label,
  secure = true,
  ...inputProps
}: ThemedMaskedInputProps & WrappedFieldProps) => {
  const { inputStyle } = useContext(ThemeContext);
  const [secured, { toggle: toggleMask }] = useToggle(secure);
  const displayValue = secured ? getSecuredMaskedValue(input.value as string, mask) : input.value;

  const hasError = !!(error && submitFailed);

  // The material input needs special handling because of the way it is positioned.
  // We allow the display input to be focusable, but instantly transfer focus to
  // the masked input.
  const [displayInputFocused, { enable: focusDisplayInput, disable: blurDisplayInput }] =
    useToggle(false);

  switch (inputStyle) {
    case InputStyles.Material:
      return (
        <MaterialContainer>
          <Container>
            {secure && (
              <MaterialButtonContainer>
                <ToggleButton
                  type='button'
                  width='icon'
                  variant='basic'
                  onClick={toggleMask}
                  tabIndex={-1}
                >
                  {secured ? <EyeOffIcon /> : <EyeIcon />}
                </ToggleButton>
              </MaterialButtonContainer>
            )}
            <StyledMaterialThemedMaskedInput
              {...inputProps}
              {...input}
              mask={mask}
              autoComplete='no-autocomplete'
              guide={false}
              placeholder={undefined}
              // Change the placeholder char to unicode En Quad which allows the
              // input of the default placeholder char underscore without crashing
              placeholderChar={'\u2000'}
              onFocus={focusDisplayInput}
              onBlur={blurDisplayInput}
            />
            <DisplayMaterialInput
              value={displayValue}
              readOnly
              disabled={inputProps.disabled}
              hasError={hasError}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              labelText={label}
              tabIndex={-1}
              type='text'
              data-test='themed-masked-input__display-input'
              focused={displayInputFocused}
            />
            {hasError && <MaterialFormError>{error}</MaterialFormError>}
          </Container>
        </MaterialContainer>
      );
    default:
      return (
        <OutlinedContainer>
          {label && (
            <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: input.name }}>
              {label}
            </Markdown>
          )}
          <Container>
            {secure && (
              <ButtonContainer>
                <ToggleButton
                  type='button'
                  width='icon'
                  variant='basic'
                  onClick={toggleMask}
                  tabIndex={-1}
                >
                  {secured ? <EyeOffIcon /> : <EyeIcon />}
                </ToggleButton>
              </ButtonContainer>
            )}
            <StyledThemedMaskedInput
              {...inputProps}
              {...input}
              mask={mask}
              autoComplete='no-autocomplete'
              guide={false}
              placeholder={undefined}
              // Change the placeholder char to unicode En Quad which allows the
              // input of the default placeholder char underscore without crashing
              placeholderChar={'\u2000'}
            />
            <DisplayInput
              value={displayValue}
              readOnly
              disabled={inputProps.disabled}
              hasError={hasError}
              placeholder={inputProps.placeholder}
              tabIndex={-1}
              type='text'
              data-test='themed-masked-input__display-input'
            />
          </Container>
          {hasError && <FormError>{error}</FormError>}
        </OutlinedContainer>
      );
  }
};
export default ThemedMaskedInput;

const MIDDOT = '\u2022';

export function getSecuredMaskedValue(value: string, mask: ThemedMaskedInputProps['mask']): string {
  if (Array.isArray(mask)) {
    return value
      .split('')
      .map((char, i) => (mask[i] instanceof RegExp ? MIDDOT : char))
      .join('');
  } else {
    return MIDDOT.repeat(value.length);
  }
}
